import styled from 'styled-components';

import Button from 'components/shared/Button';
import { simpleDateAtTime } from 'lib/date';
import { PortalRetrievalStatus } from 'services/portalExtension';
import { BodySmall, Label } from 'styles/typography';
import CheckIcon from 'svg/CheckIcon';
import XIcon from 'svg/XIcon';

import { ScanCard as ScanCardModel } from './scanCards';

interface Props {
  card: ScanCardModel;
  onOpenResults: (card: ScanCardModel) => void;
  // Set only when this browser is the thing that sweeps this portal and the run is still waiting
  // to be driven. Absent for headless runs, for finished ones, and where there is no extension.
  onRunInBrowser?: () => void;
  // The live status of a sweep running right now, from the extension.
  runStatus?: PortalRetrievalStatus | 'starting';
}

// The extension's own status vocabulary, said in words a coordinator can act on.
const RUN_STATUS_COPY: Record<string, string> = {
  starting: 'Opening the portal…',
  running: 'Scanning the portal…',
  waiting: 'Waiting on the portal…',
  login: 'Signing in…',
};

/**
 * One scan. The three states the design asks for plus `running`, and the ONLY one that opens
 * anything is a success with rows behind it — a failed scan is terminal (nothing to look at) and
 * a scan that found nothing must not link to an empty table.
 */
export default function ScanCard({ card, onOpenResults, onRunInBrowser, runStatus }: Props) {
  const sweeping = runStatus !== undefined;
  const failed = card.state === 'failed';

  return (
    <Card $failed={failed} data-testid='scan-card' data-state={card.state}>
      <StatusBadge $failed={failed} $muted={card.state === 'running'} aria-hidden>
        {failed ? <XIcon width={14} height={14} /> : <CheckIcon width={14} height={14} fill='currentColor' />}
      </StatusBadge>
      <Content>
        <Title $failed={failed} data-testid='scan-card-title'>
          {card.title}
        </Title>
        <MetaRow>
          {card.canOpenResults ? (
            <ResultsLink type='button' onClick={() => onOpenResults(card)} data-testid='scan-card-results'>
              {card.detail}
            </ResultsLink>
          ) : (
            <Detail data-testid='scan-card-detail'>
              {sweeping ? (RUN_STATUS_COPY[runStatus] ?? 'Scanning the portal…') : card.detail}
            </Detail>
          )}
          {/* The completion time, and only the completion time. A scan still running has none,
              and saying "scanned on <the time it was requested>" would be a different claim. */}
          {card.completedAt && (
            <Timestamp data-testid='scan-card-time'>
              •&nbsp;&nbsp;
              {failed ? 'Portal scan failed on ' : 'Portal successfully scanned on '}
              {simpleDateAtTime(card.completedAt)}
            </Timestamp>
          )}
        </MetaRow>
        {/* In extension mode the run exists but nothing has swept yet — the sweep happens in this
            browser. Without this the card would sit at "running" indefinitely. */}
        {onRunInBrowser && (
          <Actions>
            <Button
              size='small'
              loading={sweeping}
              disabled={sweeping}
              onClick={onRunInBrowser}
              data-testid='scan-card-run'>
              {card.state === 'failed' ? 'Try this scan again' : 'Run this scan'}
            </Button>
          </Actions>
        )}
      </Content>
    </Card>
  );
}

const Card = styled.div<{ $failed: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 24px;
  padding: 24px;
  border-radius: ${({ theme }) => theme.dimensions.borderRadius};
  background-color: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme, $failed }) => ($failed ? theme.colors.red100 : theme.colors.black10)};
`;

const StatusBadge = styled.span<{ $failed: boolean; $muted: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background-color: ${({ theme, $failed, $muted }) =>
    $failed ? theme.colors.red10 : $muted ? theme.colors.black5 : theme.colors.green10};
  color: ${({ theme, $failed, $muted }) =>
    $failed ? theme.colors.red100 : $muted ? theme.colors.black50 : theme.colors.green100};
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
`;

const Title = styled(Label)<{ $failed: boolean }>`
  font-size: 15px;
  color: ${({ theme, $failed }) => ($failed ? theme.colors.red100 : theme.colors.black100)};
`;

const MetaRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 6px;
  flex-wrap: wrap;
`;

const Timestamp = styled(BodySmall)`
  color: ${({ theme }) => theme.colors.black50};
`;

const Detail = styled(BodySmall)`
  color: ${({ theme }) => theme.colors.black50};
`;

const ResultsLink = styled.button`
  font-family: ${({ theme }) => theme.textStyles.base.fontFamily};
  font-size: 13px;
  color: ${({ theme }) => theme.colors.blue100};
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  text-align: left;
  text-decoration: underline;
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-start;
  margin-top: 4px;
`;
